import React from 'react'
import styled from '@emotion/styled'
import { useOutsideClick } from 'rooks'
import { useQuery } from '@tanstack/react-query'
import { toMoney } from '@mobi/utils/money'
import { colors, font, radius, shadow, spacing } from '@mobi/component-library/Theme/Common'
import { Icon } from '@mobi/component-library/Common/V2/Icon'
import { Tooltip } from '@mobi/component-library/Common/V2/Tooltip'
import { Last10WinLossIndicator } from '@core/Areas/FollowMe/Components/Common/Last10WinLossIndicator'
import { followMeProfileStatsByIdQuery } from '@core/Areas/FollowMe/helpers/queries'
import type { FollowMeProfile } from '@core/Areas/FollowMe/types'
import { formatStatValue } from '@core/Areas/FollowMe/helpers/formatStatValue'
import { FlameStreakBadge } from '@core/Areas/FollowMe/Components/Common/FlameStreak'
import { useOnboardingTooltips } from '@core/Areas/FollowMe/hooks/useOnboardingTooltips'

const enum LocalConstants {
  FlameStreakClass = 'profile-stats__flame-streak',
}

export const ProfileStats = ({ id }: Pick<FollowMeProfile, 'id'>) => {
  const flameRef = React.useRef<HTMLSpanElement>(null)

  const { data } = useQuery(followMeProfileStatsByIdQuery(id))
  const streakNumber = data?.currentWinStreak ?? 0

  const { currentTooltipId, onDismissed } = useOnboardingTooltips('bet-loop-stats-flame')

  const shouldDisplayWinStreak = streakNumber >= 3
  const shouldShowTooltip = shouldDisplayWinStreak && currentTooltipId === 'bet-loop-stats-flame'
  useOutsideClick(flameRef, () => {
    if (shouldShowTooltip) onDismissed('bet-loop-stats-flame')
  })

  return (
    <WrapperStyled>
      <div className='profile-stats__last10'>
        <div>
          <dl>
            <dt>Last 10</dt>
            <dd>Shared Bets</dd>
          </dl>

          {shouldDisplayWinStreak && (
            <FlameStreakBadge
              ref={flameRef}
              value={streakNumber}
              className={LocalConstants.FlameStreakClass}
            />
          )}

          {shouldShowTooltip && (
            <Tooltip
              color='blue'
              hasTooltipBeenSeen={() => false}
              shouldHideCrossIcon
              flipElement={true}
              preferredPlacement='left'
              triggerElRef={flameRef}
              tooltipId='bet-loop-stats-flame'
              offsetYPx={10}
              shouldOnlyRenderSingleInstance
              onDismissed={() => onDismissed('bet-loop-stats-flame')}
            >
              <div style={{ textAlign: 'center', minWidth: '10rem' }}>Active win streak</div>
            </Tooltip>
          )}
        </div>

        <div>
          {shouldDisplayWinStreak && (
            <FlameStreakBadge
              ref={flameRef}
              value={streakNumber}
              className={LocalConstants.FlameStreakClass}
            />
          )}

          <Last10WinLossIndicator
            size='L'
            currentStreak={data?.currentWinStreak ?? 0}
            last10Results={data?.lastTenResults ?? []}
          />
        </div>
      </div>

      <div>
        <div className='profile-stats__list-item'>
          <Icon name='DuocolorTrophy01' size='2rem' color='black' />
          <dl>
            <dt>Total Wins</dt>
          </dl>
          <span className='profile-stats__stat-value'>{data?.totalWins}</span>
        </div>

        <div className='profile-stats__list-item'>
          <Icon name='CustomEllipse12' size='2rem' color='black' />
          <dl>
            <dt>Best Win Streak</dt>
          </dl>
          <span className='profile-stats__stat-value'>{data?.bestWinStreak}</span>
        </div>

        <div className='profile-stats__list-item'>
          <Icon name='DuocolorLineChartUp01' size='2rem' color='black' />
          <dl>
            <dt>Average Win Odds</dt>
            <dd>Fixed Odds Only</dd>
          </dl>
          <span className='profile-stats__stat-value'>
            {data?.avgOdds ? toMoney(data.avgOdds) : '0'}
          </span>
        </div>

        <div className='profile-stats__list-item'>
          <Icon name='DuocolorAward03' size='2rem' color='black' />
          <dl>
            <dt>Biggest Win Odds</dt>
            <dd>Fixed Odds Only</dd>
          </dl>
          <span className='profile-stats__stat-value'>
            {data?.highestOddsWin ? toMoney(data.highestOddsWin) : '0'}
          </span>
        </div>

        <div className='profile-stats__list-item'>
          <Icon name='DuocolorCopy02' size='2rem' color='black' />
          <dl>
            <dt>Total Copies</dt>
          </dl>
          <span className='profile-stats__stat-value'>{formatStatValue(data?.copyCount)}</span>
        </div>
      </div>
    </WrapperStyled>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  padding: `${spacing.smx1} ${spacing.md}`,

  '> .profile-stats__last10': {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.smx1,
    alignSelf: 'stretch',
    padding: spacing.sm,
    borderRadius: radius.lgx2,
    border: `1px solid ${colors.neutral[200]}`,
    background: colors.white,
    marginBottom: '0.6rem',
    boxShadow: shadow.xs,

    '> div:nth-of-type(1)': {
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: spacing.smx1,

      '> dl': {
        display: 'flex',
        flexDirection: 'column',
        margin: 0,
        fontFamily: font.family.primary,

        dt: {
          fontWeight: font.weight.semibold,
          fontSize: font.size.lg.fontSize,
          letterSpacing: font.size.lg.letterSpacing,
        },

        dd: {
          margin: 0,
          fontSize: font.size.md.fontSize,
          letterSpacing: font.size.md.letterSpacing,
          lineHeight: font.size.md.lineHeight,
          color: colors.neutral[800],
        },
      },
    },

    '> div': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      alignSelf: 'stretch',
    },

    '> div:nth-of-type(2)': {
      [`.${LocalConstants.FlameStreakClass}`]: {
        display: 'none',
      },
    },

    '@media (min-width: 547px)': {
      flexDirection: 'row',
      flexWrap: 'wrap',
      padding: spacing.sm,
      gap: spacing.md,
      alignItems: 'center',
      alignSelf: 'stretch',
      justifyContent: 'space-between',

      '> div:nth-of-type(1)': {
        [`.${LocalConstants.FlameStreakClass}`]: {
          display: 'none',
        },
      },

      '> div:nth-of-type(2)': {
        paddingRight: spacing.smx2,
        gap: spacing.smx1,
        justifyContent: 'flex-end',

        [`.${LocalConstants.FlameStreakClass}`]: {
          display: 'flex',
        },
      },
    },
  },

  '> div:nth-of-type(2)': {
    boxShadow: shadow.sm,
    borderRadius: spacing.md,
    overflow: 'hidden',

    '.profile-stats__list-item': {
      background: colors.white,
      padding: `${spacing.sm} ${spacing.md}`,
      borderBottom: `1px solid ${colors.neutral[200]}`,
      fontFamily: font.family.primary,
      display: 'flex',
      alignItems: 'center',
      gap: spacing.md,

      '&:first-of-type': {
        borderTopLeftRadius: spacing.md,
        borderTopRightRadius: spacing.md,
      },

      '&:last-of-type': {
        borderBottomLeftRadius: spacing.md,
        borderBottomRightRadius: spacing.md,
      },

      dl: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: '3rem',
        margin: 0,

        dt: {
          fontWeight: font.weight.semibold,
          fontSize: font.size.lg.fontSize,
          letterSpacing: font.size.lg.letterSpacing,
        },

        dd: {
          margin: 0,
          fontSize: font.size.md.fontSize,
          letterSpacing: font.size.md.letterSpacing,
          lineHeight: font.size.md.lineHeight,
          color: colors.neutral[800],
        },
      },

      '> span:last-of-type': {
        marginLeft: 'auto',
        fontWeight: font.weight.black,
        color: colors.neutral[900],
        fontFamily: font.family.primary,
        fontSize: font.size.xl4.fontSize,
        letterSpacing: font.size.xl4.letterSpacing,
        fontStyle: 'italic',
        textAlign: 'center',
      },
    },
  },
})
